import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material'
import { useState } from 'react'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { buildPrintPreviewHtml, sanitizePdfFileNamePart } from '@/utils/printPreview'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'

// Access port: rpCtrlSalesRO (Release Order / Receive Order) + subreport
// rpCtrlSalesRO_sub. frmCtrlSalesQryRO print → DoCmd.OpenReport "rpCtrlSalesRO".
// PRESENTATIONAL dialog: HTML đến qua props (parent fetch). Mirror DoPrintDialog:
// iframe srcDoc preview + nút Print → printAndUploadReport.

interface ReceiveOrderPrintDialogProps {
  open: boolean
  /** Tiêu đề dialog (vd "Print Preview - RO (FR01/INV4064.F)"). */
  title: string
  /** HTML preview (đã do parent fetch). */
  html: string
  /** Đang fetch HTML (overlay spinner). */
  loading: boolean
  canEdit?: boolean
  onClose: () => void
}

export default function ReceiveOrderPrintDialog({
  open,
  title,
  html,
  loading,
  canEdit = true,
  onClose,
}: ReceiveOrderPrintDialogProps) {
  const [toastMessage, setToastMessage] = useState('')
  const iframeTitle = 'RO Print Preview'
  // Release Order = report có bảng chi tiết co giãn → BE trả nhiều tờ `.ro-page`
  // (bảng nhiều dòng tự break trang). `pageSelector` cho mỗi .ro-page thành 1 tờ A4
  // riêng trên nền xám (preview tách trang = PDF).
  const previewHtml = html
    ? buildPrintPreviewHtml(html, {
        styleId: 'ro-print-preview-overrides',
        pageSelector: '.ro-page',
        pagePadding: '9mm 6mm',
        extraCss: '.ro-page + .ro-page { break-before: page; page-break-before: always; }',
      })
    : ''

  return (
    <>
      {open && loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.35)',
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}
      <Dialog
        open={open && Boolean(html)}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box
            component="span"
            title={title}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            RO
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} size="small">
              Cancel
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                size="small"
                disabled={!html}
                onClick={() => {
                  const idPart = title.replace(/^.*\(([^)]+)\).*$/, '$1')
                  const printTitle = `${sanitizePdfFileNamePart(idPart || 'release_order')}.pdf`
                  void printAndUploadReport({
                    iframeTitle,
                    reportName: 'rpCtrlSalesRO',
                    printTitle,
                    html: previewHtml,
                    onUploadError: setToastMessage,
                  })
                }}
              >
                Print
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title={iframeTitle}
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2500}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </>
  )
}
